<template>
  <div class="auditoria-wrap">

    <v-toolbar color="white" flat elevation="0" class="page-toolbar" height="80">
      <v-toolbar-title class="d-flex flex-column justify-center py-2">
        <div class="d-flex align-center">
          <div class="page-icon-wrapper">
            <v-icon color="#023449" size="26">mdi-shield-search</v-icon>
          </div>
          <div class="ml-3">
            <span class="page-title">Auditoria Wolihub</span>
            <p class="page-subtitle mb-0">Acompanhamento de indicadores, exceções e inconsistências da integração Wolihub</p>
          </div>
        </div>
      </v-toolbar-title>
    </v-toolbar>

    <v-expansion-panels v-model="painelAberto" flat class="indicadores-panels">

      <!-- ══ Indicador: Créditos Complementares ══════════════════════════════ -->
      <v-expansion-panel class="indicador-panel">

        <!-- ── Header do painel (visível quando minimizado) ─────────────────── -->
        <v-expansion-panel-header class="indicador-header" hide-actions>
          <template #default="{ open }">
            <div class="indicador-header-content">

              <!-- Lado esquerdo: ícone + título + chevron -->
              <div class="indicador-titulo-wrap">
                <v-icon size="18" color="#4a7a96" class="mr-2">mdi-credit-card-check-outline</v-icon>
                <span class="indicador-titulo">Créditos Complementares</span>
                <v-icon
                    size="16" color="#94a3b8"
                    class="ml-2 indicador-chevron"
                    :class="{ 'indicador-chevron--open': open }"
                >mdi-chevron-down</v-icon>
              </div>

              <!-- Centro: totais / loading / sem dados -->
              <div class="indicador-centro">
                <div v-if="grupos.length" class="indicador-resumo-wrap">
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Gerado</span>
                    <span class="header-chip-valor">{{ totaisGlobais.gerado | formataDinheiro }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Esperado</span>
                    <span class="header-chip-valor">{{ totaisGlobais.esperado | formataDinheiro }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip" :class="{
                    'header-chip--pos': totaisGlobais.diferenca > 0.01,
                    'header-chip--neg': totaisGlobais.diferenca < -0.01,
                  }">
                    <span class="header-chip-label">Diferença</span>
                    <span class="header-chip-valor">{{ totaisGlobais.diferenca | formataDinheiro }}</span>
                  </v-chip>
                </div>
                <div v-else-if="loading && !open" class="indicador-carregando">
                  <v-progress-circular indeterminate size="14" width="2" color="#4a7a96" class="mr-2" />
                  <span>Carregando...</span>
                </div>
                <div v-else-if="!open" class="indicador-sem-dados">
                  <v-icon size="13" color="#b0c4d4" class="mr-1">mdi-information-outline</v-icon>
                  <span>Aplique os filtros para carregar</span>
                </div>
              </div>

              <!-- Botão atualizar (sempre à direita) -->
              <v-btn icon x-small class="indicador-refresh" :loading="loading" :disabled="loading" @click.stop="buscar">
                <v-icon size="15" color="#7a9ab0">mdi-refresh</v-icon>
              </v-btn>

            </div>
          </template>
        </v-expansion-panel-header>

        <!-- ── Conteúdo expandido ────────────────────────────────────────────── -->
        <v-expansion-panel-content class="indicador-content">

          <!-- Filtros -->
          <v-card elevation="0" outlined class="mb-3" style="border-radius: 10px;">
            <v-card-text class="pt-3 pb-2 px-2">
              <v-form @submit.prevent="buscar">
                <v-row align="center" no-gutters>

                  <!-- Filial -->
                  <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                    <SeletorFiliais
                        :filiais="filiaisWolihub"
                        v-model="busca.idfiliais"
                        label="Filial"
                        :clearable="true"
                        :shortcuts="['todas']"
                        :disabled="loading"
                    />
                  </v-col>

                  <!-- Período -->
                  <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                    <SeletorPeriodo
                        :disabled="loading"
                        v-model="busca.periodo"
                        label="Período"
                        :shortcuts="['periods', 'weeks', 'months']"
                    />
                  </v-col>

                  <!-- Cliente -->
                  <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                    <SeletorCliente
                        v-model="busca.cliente"
                        label="Cliente"
                        :disabled="loading"
                    />
                  </v-col>

                  <!-- Nº Pedido -->
                  <v-col cols="12" sm="2" class="px-1 mb-2 mb-sm-0">
                    <v-text-field
                        v-model="busca.idorcamento"
                        label="Nº Pedido"
                        outlined dense clearable hide-details
                        :disabled="loading"
                        type="number"
                        prepend-inner-icon="mdi-file-document-outline"
                        class="filtro-field"
                    />
                  </v-col>

                  <!-- Buscar -->
                  <v-col cols="12" sm="auto" class="px-1">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                            icon
                            class="primary elevation-0 d-none d-sm-flex mt-n1"
                            :loading="loading" :disabled="loading"
                            style="border-radius: 50%; height: 32px; width: 32px;"
                            v-bind="attrs" v-on="on"
                            @click="buscar"
                        >
                          <v-icon small color="white">mdi-magnify</v-icon>
                        </v-btn>
                      </template>
                      <span>Buscar</span>
                    </v-tooltip>
                    <v-btn
                        class="primary elevation-0 d-flex d-sm-none"
                        block :loading="loading" :disabled="loading"
                        style="border-radius: 8px; height: 40px;"
                        @click="buscar"
                    >
                      <v-icon small left>mdi-magnify</v-icon>
                      Buscar
                    </v-btn>
                  </v-col>

                </v-row>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- Chips de status -->
          <div v-if="grupos.length" class="chips-status-wrap">
            <div
                v-for="s in resumo" :key="s.status"
                class="resumo-chip resumo-chip--mini"
                :class="[`resumo-chip--${s.cor}`, { 'resumo-chip--ativo': filtroStatus === s.status }]"
                @click="toggleFiltroStatus(s.status)"
            >
              <span class="resumo-chip-label">{{ s.label }}</span>
              <span class="resumo-chip-count">{{ s.count }}</span>
            </div>
          </div>

          <!-- Tabela principal -->
          <v-card elevation="0" outlined class="tabela-card">
            <v-progress-linear indeterminate absolute :active="loading" color="primary" />

            <div v-if="!buscado && !loading" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-clipboard-search-outline</v-icon>
              <span class="estado-texto">Carregando...</span>
            </div>

            <div v-else-if="loading && !grupos.length" class="estado-vazio">
              <v-progress-circular indeterminate color="primary" size="36" />
              <span class="estado-texto">Carregando...</span>
            </div>

            <div v-else-if="buscado && !grupos.length" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-check-circle-outline</v-icon>
              <span class="estado-texto">Nenhum registro encontrado</span>
            </div>

            <div v-else-if="gruposFiltrados.length === 0" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-filter-outline</v-icon>
              <span class="estado-texto">Nenhum pedido com este status</span>
              <v-btn text small color="primary" @click="filtroStatus = null">Limpar filtro</v-btn>
            </div>

            <div v-else class="tabela-scroll-wrap">
              <table class="audit-table">
                <thead>
                <tr>
                  <th class="th-expand"></th>
                  <th class="th-sort" @click="ordenarCreditos('status_geral')">
                    Status <span class="sort-icon">{{ sortIcon('sortCreditos', 'status_geral') }}</span>
                  </th>
                  <th class="centro th-sort" style="width:56px" @click="ordenarCreditos('idempresa')">
                    Filial <span class="sort-icon">{{ sortIcon('sortCreditos', 'idempresa') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarCreditos('idorcamento')">
                    Pedido <span class="sort-icon">{{ sortIcon('sortCreditos', 'idorcamento') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarCreditos('idclifor')">
                    Cliente <span class="sort-icon">{{ sortIcon('sortCreditos', 'idclifor') }}</span>
                  </th>
                  <th class="centro">Enviado</th>
                  <th class="centro">Dev. E-book</th>
                  <th class="num th-sort" @click="ordenarCreditos('total_credito_gerado')">
                    Créd. Gerado <span class="sort-icon">{{ sortIcon('sortCreditos', 'total_credito_gerado') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarCreditos('total_credito_esperado')">
                    Créd. Esperado <span class="sort-icon">{{ sortIcon('sortCreditos', 'total_credito_esperado') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarCreditos('total_diferenca')">
                    Diferença <span class="sort-icon">{{ sortIcon('sortCreditos', 'total_diferenca') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarCreditos('itens')">
                    Itens <span class="sort-icon">{{ sortIcon('sortCreditos', 'itens') }}</span>
                  </th>
                </tr>
                </thead>
                <tbody>
                <template v-for="(grupo, i) in gruposPaginados">
                  <!-- Linha principal do pedido -->
                  <tr
                      :key="`g-${i}`"
                      class="grupo-row"
                      :class="`row-status--${statusCor(grupo.status_geral)}`"
                      @click="toggleExpand(i)"
                  >
                    <td class="expand-td">
                      <v-icon size="16" color="#7a9ab0" class="expand-icon" :class="{ 'expand-icon--open': expandido[i] }">
                        mdi-chevron-right
                      </v-icon>
                    </td>
                    <td class="status-td">
                        <span class="status-badge" :class="`status-badge--${statusCor(grupo.status_geral)}`">
                          {{ statusLabel(grupo.status_geral) }}
                        </span>
                      <v-tooltip right v-if="grupo.status_geral === 'PENDENTE'">
                        <template #activator="{ on }">
                          <v-btn
                              icon x-small class="ml-1"
                              :loading="reprocessando[i]"
                              v-on="on"
                              @click.stop="reprocessar(grupo, i)"
                          >
                            <v-icon size="14" color="#d97706">mdi-refresh</v-icon>
                          </v-btn>
                        </template>
                        <span>Reprocessar crédito complementar</span>
                      </v-tooltip>
                    </td>
                    <td class="centro id-cell">{{ grupo.idempresa }}</td>
                    <td class="num id-cell font-weight-medium">{{ grupo.idorcamento }}</td>
                    <td class="num id-cell">{{ grupo.idclifor || '—' }}</td>
                    <td class="centro">
                      <v-icon size="14" :color="grupo.enviado_wolihub === 'Sim' ? '#22c55e' : '#94a3b8'">
                        {{ grupo.enviado_wolihub === 'Sim' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                    </td>
                    <td class="centro">
                      <v-icon size="14" :color="grupo.ebook_devolvido === 'Sim' ? '#f59e0b' : '#94a3b8'">
                        {{ grupo.ebook_devolvido === 'Sim' ? 'mdi-book-arrow-left-outline' : 'mdi-circle-outline' }}
                      </v-icon>
                    </td>
                    <td class="num moeda">{{ grupo.total_credito_gerado | formataDinheiro }}</td>
                    <td class="num moeda">{{ grupo.total_credito_esperado | formataDinheiro }}</td>
                    <td class="num moeda" :class="diferencaClass(grupo.total_diferenca)">
                      {{ grupo.total_diferenca | formataDinheiro }}
                    </td>
                    <td class="num">
                      <span class="itens-count">{{ (grupo.itens || []).length }}</span>
                    </td>
                  </tr>

                  <!-- Linha expandida com produtos -->
                  <tr v-if="expandido[i]" :key="`e-${i}`" class="expand-row">
                    <td colspan="11" class="expand-cell">
                      <table class="prod-table">
                        <thead>
                        <tr>
                          <th>Status</th>
                          <th>Dt. Devolução</th>
                          <th>Produto</th>
                          <th class="num">Desc. E-book</th>
                          <th class="num">Qtd. Log</th>
                          <th class="num">Qtd. Dev.</th>
                          <th class="num">Val. Dev.</th>
                          <th class="num">Créd. Gerado</th>
                          <th class="num">Créd. Esperado</th>
                          <th class="num">Diferença</th>
                          <th>Planilha</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, j) in (grupo.itens || [])" :key="j" :class="`prod-row--${statusCor(item.status)}`">
                          <td>
                                <span class="status-badge status-badge--sm" :class="`status-badge--${statusCor(item.status)}`">
                                  {{ statusLabel(item.status) }}
                                </span>
                          </td>
                          <td class="data-cell">{{ item.dt_devolucao || '—' }}</td>
                          <td class="prod-cod">{{ item.idsubproduto }}</td>
                          <td class="num moeda">{{ item.desconto_ebook_aplicado | formataDinheiro }}</td>
                          <td class="num">{{ formataNumero(item.qtd_log) }}</td>
                          <td class="num">{{ formataNumero(item.qtd_devolvida) }}</td>
                          <td class="num moeda">{{ item.val_devolvido | formataDinheiro }}</td>
                          <td class="num moeda">{{ item.credito_gerado | formataDinheiro }}</td>
                          <td class="num moeda">{{ item.credito_esperado | formataDinheiro }}</td>
                          <td class="num moeda" :class="diferencaClass(item.diferenca)">{{ item.diferenca | formataDinheiro }}</td>
                          <td class="planilha-cell">{{ item.idplanilha_origem || '—' }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
                </tbody>
                <tfoot v-if="grupos.length">
                <tr class="tfoot-row">
                  <td colspan="7" class="tfoot-label">Total</td>
                  <td class="num moeda tfoot-val">{{ totaisGlobais.gerado | formataDinheiro }}</td>
                  <td class="num moeda tfoot-val">{{ totaisGlobais.esperado | formataDinheiro }}</td>
                  <td class="num moeda tfoot-val" :class="diferencaClass(totaisGlobais.diferenca)">{{ totaisGlobais.diferenca | formataDinheiro }}</td>
                  <td></td>
                </tr>
                </tfoot>
              </table>
            </div>

            <div v-if="grupos.length" class="tabela-footer">
              <span>
                {{ gruposFiltrados.length }}<template v-if="filtroStatus"> de {{ grupos.length }}</template>
                pedido{{ gruposFiltrados.length !== 1 ? 's' : '' }}
                · {{ totalItensFiltrados }} produto{{ totalItensFiltrados !== 1 ? 's' : '' }}
              </span>
              <v-pagination
                  v-if="totalPaginasCreditos > 1"
                  v-model="paginaCreditos"
                  :length="totalPaginasCreditos"
                  :total-visible="7"
                  class="tabela-paginacao"
              />
            </div>
          </v-card>

        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- ══ Indicador: Saldo de Estoque ══════════════════════════════════════ -->
      <v-expansion-panel class="indicador-panel">

        <v-expansion-panel-header class="indicador-header" hide-actions>
          <template #default="{ open }">
            <div class="indicador-header-content">

              <div class="indicador-titulo-wrap">
                <v-icon size="18" color="#4a7a96" class="mr-2">mdi-package-variant-closed</v-icon>
                <span class="indicador-titulo">Saldo de E-books em Estoque</span>
                <v-icon size="16" color="#94a3b8" class="ml-2 indicador-chevron" :class="{ 'indicador-chevron--open': open }">mdi-chevron-down</v-icon>
              </div>

              <!-- Centro: totais / loading / sem dados -->
              <div class="indicador-centro">
                <div v-if="estoque.length" class="indicador-resumo-wrap">
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Filiais</span>
                    <span class="header-chip-valor">{{ estoque.length }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Qtd. E-books</span>
                    <span class="header-chip-valor">{{ totalEstoqueQtd }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Vl. Estoque</span>
                    <span class="header-chip-valor">{{ totalEstoqueVal | formataDinheiro }}</span>
                  </v-chip>
                  <v-chip v-if="estoqueCriticas > 0" x-small class="header-chip header-chip--neg">
                    <span class="header-chip-label">Críticas</span>
                    <span class="header-chip-valor">{{ estoqueCriticas }}</span>
                  </v-chip>
                </div>
                <div v-else-if="loadingEstoque && !open" class="indicador-carregando">
                  <v-progress-circular indeterminate size="14" width="2" color="#4a7a96" class="mr-2" />
                  <span>Carregando...</span>
                </div>
                <div v-else-if="!open" class="indicador-sem-dados">
                  <v-icon size="13" color="#b0c4d4" class="mr-1">mdi-information-outline</v-icon>
                  <span>Sem dados</span>
                </div>
              </div>

              <!-- Botão atualizar (sempre à direita) -->
              <v-btn icon x-small class="indicador-refresh" :loading="loadingEstoque" :disabled="loadingEstoque" @click.stop="buscarEstoque">
                <v-icon size="15" color="#7a9ab0">mdi-refresh</v-icon>
              </v-btn>

            </div>
          </template>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="indicador-content">
          <v-card elevation="0" outlined class="tabela-card">
            <v-progress-linear indeterminate absolute :active="loadingEstoque" color="primary" />

            <div v-if="loadingEstoque && !estoque.length" class="estado-vazio">
              <v-progress-circular indeterminate color="primary" size="36" />
              <span class="estado-texto">Carregando...</span>
            </div>

            <div v-else-if="!estoque.length" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-package-variant</v-icon>
              <span class="estado-texto">Nenhum dado de estoque encontrado</span>
            </div>

            <div v-else class="tabela-scroll-wrap">
              <table class="audit-table">
                <thead>
                <tr>
                  <th class="centro" style="width:52px">Filial</th>
                  <th>Nome</th>
                  <th class="num th-sort" @click="ordenarEstoque('qtd_estoque')">
                    Qtd. Estoque <span class="sort-icon">{{ sortIcon('sortEstoque', 'qtd_estoque') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarEstoque('val_estoque')">
                    Valor Estoque <span class="sort-icon">{{ sortIcon('sortEstoque', 'val_estoque') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarEstoque('qtd_vendida_30d')">
                    Qtd. Vendida <span class="sort-icon">{{ sortIcon('sortEstoque', 'qtd_vendida_30d') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarEstoque('media_diaria')">
                    Média/Dia <span class="sort-icon">{{ sortIcon('sortEstoque', 'media_diaria') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarEstoque('dias_estoque')">
                    Dias Est. <span class="sort-icon">{{ sortIcon('sortEstoque', 'dias_estoque') }}</span>
                  </th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                <tr
                    v-for="(item, i) in estoqueOrdenado" :key="i"
                    :class="estoqueRowClass(item)"
                >
                  <td class="centro id-cell">{{ item.idempresa }}</td>
                  <td style="font-size:12px; color:#2c5f7a; font-weight:500;">{{ item.filial }}</td>
                  <td class="num font-weight-medium">{{ formataNumero(item.qtd_estoque) }}</td>
                  <td class="num moeda">{{ item.val_estoque | formataDinheiro }}</td>
                  <td class="num">{{ formataNumero(item.qtd_vendida_30d) }}</td>
                  <td class="num">
                    <span v-if="item.media_diaria > 0">{{ item.media_diaria.toFixed(1) }}/dia</span>
                    <span v-else class="text--disabled">—</span>
                  </td>
                  <td class="num">
                      <span v-if="item.dias_estoque != null" class="dias-badge" :class="diasBadgeClass(item.dias_estoque)">
                        {{ item.dias_estoque }}d
                      </span>
                    <span v-else class="text--disabled">—</span>
                  </td>
                  <td>
                      <span class="status-badge" :class="estoqueStatusBadge(item)">
                        {{ estoqueStatusLabel(item) }}
                      </span>
                  </td>
                </tr>
                </tbody>
                <tfoot v-if="estoque.length">
                <tr class="tfoot-row">
                  <td colspan="2" class="tfoot-label">Total</td>
                  <td class="num tfoot-val">{{ formataNumero(totalEstoqueQtd) }}</td>
                  <td class="num moeda tfoot-val">{{ totalEstoqueVal | formataDinheiro }}</td>
                  <td class="num tfoot-val">{{ formataNumero(totalEstoqueVendida) }}</td>
                  <td colspan="3"></td>
                </tr>
                </tfoot>
              </table>
            </div>

            <div v-if="estoque.length" class="tabela-footer">
              <span>{{ estoque.length }} filial{{ estoque.length !== 1 ? 'is' : '' }} · média dos últimos 30 dias</span>
            </div>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- ══ Indicador: Saldo de E-books por Cliente ════════════════════════ -->
      <v-expansion-panel class="indicador-panel">

        <v-expansion-panel-header class="indicador-header" hide-actions>
          <template #default="{ open }">
            <div class="indicador-header-content">

              <div class="indicador-titulo-wrap">
                <v-icon size="18" color="#4a7a96" class="mr-2">mdi-account-group-outline</v-icon>
                <span class="indicador-titulo">Limite de E-books por Cliente</span>
                <v-icon size="16" color="#94a3b8" class="ml-2 indicador-chevron" :class="{ 'indicador-chevron--open': open }">mdi-chevron-down</v-icon>
              </div>

              <div class="indicador-centro">
                <div v-if="saldoClientes.length" class="indicador-resumo-wrap">
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Top Clientes</span>
                    <span class="header-chip-valor">{{ saldoClientes.length }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip" :class="{ 'header-chip--neg': saldoClientesAlerta > 0 }">
                    <span class="header-chip-label">Próximos ao limite</span>
                    <span class="header-chip-valor">{{ saldoClientesAlerta }}</span>
                  </v-chip>
                </div>
                <div v-else-if="loadingSaldoClientes && !open" class="indicador-carregando">
                  <v-progress-circular indeterminate size="14" width="2" color="#4a7a96" class="mr-2" />
                  <span>Carregando...</span>
                </div>
                <div v-else-if="!open" class="indicador-sem-dados">
                  <v-icon size="13" color="#b0c4d4" class="mr-1">mdi-information-outline</v-icon>
                  <span>Sem dados</span>
                </div>
              </div>

              <v-btn icon x-small class="indicador-refresh" :loading="loadingSaldoClientes" :disabled="loadingSaldoClientes" @click.stop="buscarSaldoClientes">
                <v-icon size="15" color="#7a9ab0">mdi-refresh</v-icon>
              </v-btn>

            </div>
          </template>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="indicador-content">

          <!-- Filtros -->
          <v-card elevation="0" outlined class="mb-3" style="border-radius: 10px;">
            <v-card-text class="pt-3 pb-2 px-2">
              <v-row align="center" no-gutters>
                <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                  <SeletorFiliais
                      :filiais="filiaisWolihub"
                      v-model="filtroSaldoClientes.idempresa"
                      label="Filial"
                      :clearable="true"
                      :single="true"
                      :shortcuts="[]"
                      :disabled="loadingSaldoClientes"
                  />
                </v-col>
                <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                  <SeletorCliente
                      v-model="filtroSaldoClientes.cliente"
                      label="Cliente"
                      :disabled="loadingSaldoClientes"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card elevation="0" outlined class="tabela-card">
            <v-progress-linear indeterminate absolute :active="loadingSaldoClientes" color="primary" />

            <div v-if="loadingSaldoClientes && !saldoClientes.length" class="estado-vazio">
              <v-progress-circular indeterminate color="primary" size="36" />
              <span class="estado-texto">Carregando...</span>
            </div>

            <div v-else-if="!saldoClientes.length" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-account-group-outline</v-icon>
              <span class="estado-texto">Nenhum cliente encontrado</span>
            </div>

            <div v-else class="tabela-scroll-wrap">
              <table class="audit-table">
                <thead>
                <tr>
                  <th class="num th-sort" style="width:60px" @click="ordenarSaldoClientes('idclifor')">
                    # <span class="sort-icon">{{ sortIcon('sortSaldoClientes', 'idclifor') }}</span>
                  </th>
                  <th class="th-sort" @click="ordenarSaldoClientes('nome_cliente')">
                    Cliente <span class="sort-icon">{{ sortIcon('sortSaldoClientes', 'nome_cliente') }}</span>
                  </th>
                  <th class="th-sort" @click="ordenarSaldoClientes('filial')">
                    Filial <span class="sort-icon">{{ sortIcon('sortSaldoClientes', 'filial') }}</span>
                  </th>
                  <th class="num th-sort" @click="ordenarSaldoClientes('saldo')">
                    Saldo <span class="sort-icon">{{ sortIcon('sortSaldoClientes', 'saldo') }}</span>
                  </th>
                  <th style="min-width:160px">Limite (300)</th>
                  <th class="num">Primeira Compra</th>
                  <th class="num">Últ. Atualização</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in saldoClientesPaginados" :key="i" :class="saldoClienteRowClass(item)">
                  <td class="num id-cell">{{ item.idclifor }}</td>
                  <td style="font-size:12px; font-weight:500;">{{ item.nome_cliente }}</td>
                  <td style="font-size:12px; color:#2c5f7a;">{{ item.filial }}</td>
                  <td class="num font-weight-bold">{{ item.saldo }}</td>
                  <td>
                    <div class="limite-wrap">
                      <v-progress-linear
                          :value="Math.min((item.saldo / 300) * 100, 100)"
                          :color="saldoClienteBarColor(item)"
                          height="6"
                          rounded
                          class="limite-bar"
                      />
                      <span class="limite-pct">{{ Math.round((item.saldo / 300) * 100) }}%</span>
                    </div>
                  </td>
                  <td class="num data-cell">{{ item.primeira_compra || '—' }}</td>
                  <td class="num data-cell">{{ item.ultima_atualizacao || '—' }}</td>
                </tr>
                </tbody>
                <tfoot v-if="saldoClientes.length">
                <tr class="tfoot-row">
                  <td colspan="3" class="tfoot-label">Total</td>
                  <td class="num tfoot-val">{{ totalSaldoClientes }}</td>
                  <td colspan="3"></td>
                </tr>
                </tfoot>
              </table>
            </div>

            <div v-if="saldoClientes.length" class="tabela-footer">
              <span>{{ saldoClientes.length }} cliente{{ saldoClientes.length !== 1 ? 's' : '' }} · limite: 300 e-books</span>
              <div style="display:flex; align-items:center; gap:12px;">
                <v-btn
                    v-if="temMaisSaldoClientes"
                    small text color="primary"
                    :loading="loadingMaisSaldoClientes"
                    @click="carregarMaisSaldoClientes"
                >
                  <v-icon small left>mdi-chevron-down</v-icon>
                  Carregar mais
                </v-btn>
                <v-pagination
                    v-if="totalPaginasSaldoClientes > 1"
                    v-model="paginaSaldoClientes"
                    :length="totalPaginasSaldoClientes"
                    :total-visible="7"
                    class="tabela-paginacao"
                />
              </div>
            </div>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- ══ Indicador: Exceções de Clientes ════════════════════════════════ -->
      <v-expansion-panel class="indicador-panel">

        <v-expansion-panel-header class="indicador-header" hide-actions>
          <template #default="{ open }">
            <div class="indicador-header-content">

              <div class="indicador-titulo-wrap">
                <v-icon size="18" color="#4a7a96" class="mr-2">mdi-account-cancel-outline</v-icon>
                <span class="indicador-titulo">Exceções de Clientes</span>
                <v-icon
                    size="16" color="#94a3b8"
                    class="ml-2 indicador-chevron"
                    :class="{ 'indicador-chevron--open': open }"
                >mdi-chevron-down</v-icon>
              </div>

              <!-- Centro: totais / loading / sem dados -->
              <div class="indicador-centro">
                <div v-if="excecoes.length" class="indicador-resumo-wrap">
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Filiais</span>
                    <span class="header-chip-valor">{{ excecoesPorFilial.length }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip">
                    <span class="header-chip-label">Exceções</span>
                    <span class="header-chip-valor">{{ excecoes.length }}</span>
                  </v-chip>
                </div>
                <div v-else-if="loadingExcecoes && !open" class="indicador-carregando">
                  <v-progress-circular indeterminate size="14" width="2" color="#4a7a96" class="mr-2" />
                  <span>Carregando...</span>
                </div>
                <div v-else-if="!open" class="indicador-sem-dados">
                  <v-icon size="13" color="#b0c4d4" class="mr-1">mdi-information-outline</v-icon>
                  <span>Nenhuma exceção cadastrada</span>
                </div>
              </div>

              <!-- Botão atualizar (sempre à direita) -->
              <v-btn icon x-small class="indicador-refresh" :loading="loadingExcecoes" :disabled="loadingExcecoes" @click.stop="buscarExcecoes">
                <v-icon size="15" color="#7a9ab0">mdi-refresh</v-icon>
              </v-btn>

            </div>
          </template>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="indicador-content">

          <v-card elevation="0" outlined class="tabela-card">
            <v-progress-linear indeterminate absolute :active="loadingExcecoes" color="primary" />

            <div v-if="loadingExcecoes && !excecoes.length" class="estado-vazio">
              <v-progress-circular indeterminate color="primary" size="36" />
              <span class="estado-texto">Carregando...</span>
            </div>

            <div v-else-if="!excecoes.length" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-account-check-outline</v-icon>
              <span class="estado-texto">Nenhum cliente na lista de exceção</span>
            </div>

            <div v-else class="tabela-scroll-wrap">
              <table class="audit-table">
                <thead>
                <tr>
                  <th class="th-expand"></th>
                  <th class="centro th-sort" style="width:60px" @click="ordenarExcecoes('idempresa')">
                    Cód. <span class="sort-icon">{{ sortIcon('sortExcecoes', 'idempresa') }}</span>
                  </th>
                  <th class="th-sort" @click="ordenarExcecoes('filial')">
                    Filial <span class="sort-icon">{{ sortIcon('sortExcecoes', 'filial') }}</span>
                  </th>
                  <th class="centro th-sort" style="width:80px" @click="ordenarExcecoes('clientes')">
                    Total <span class="sort-icon">{{ sortIcon('sortExcecoes', 'clientes') }}</span>
                  </th>
                </tr>
                </thead>
                <tbody>
                <template v-for="(grupo, i) in excecoesPorFilialPaginadas">
                  <tr
                      :key="`ef-${i}`"
                      class="grupo-row"
                      @click="toggleExpandExc(i)"
                  >
                    <td class="expand-td">
                      <v-icon size="16" color="#7a9ab0" class="expand-icon" :class="{ 'expand-icon--open': expandidoExc[i] }">
                        mdi-chevron-right
                      </v-icon>
                    </td>
                    <td class="centro" style="font-size:11px; color:#4a7a96; font-weight:600;">{{ grupo.idempresa || '—' }}</td>
                    <td>
                      <div class="exc-filial-cell">
                        <v-icon size="14" color="#4a7a96" class="mr-1">mdi-domain</v-icon>
                        <span class="font-weight-medium">{{ grupo.filial || 'Sem filial' }}</span>
                      </div>
                    </td>
                    <td class="centro">
                      <span class="itens-count">{{ grupo.clientes.length }}</span>
                    </td>
                  </tr>

                  <tr v-if="expandidoExc[i]" :key="`ee-${i}`" class="expand-row">
                    <td colspan="4" class="expand-cell">
                      <table class="prod-table">
                        <thead>
                        <tr>
                          <th class="num" style="width:70px"># Cliente</th>
                          <th>Nome</th>
                          <th>Usuário</th>
                          <th>Motivo</th>
                          <th>Data</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(exc, j) in grupo.clientes" :key="j">
                          <td class="num prod-cod">{{ exc.idclifor }}</td>
                          <td class="exc-nome-cell">{{ exc.nomecliente }}</td>
                          <td class="exc-usuario-cell">
                            <v-icon size="11" color="#4a7a96" class="mr-1">mdi-account-outline</v-icon>
                            {{ exc.nomeusuario || '—' }}
                          </td>
                          <td class="exc-motivo-cell">{{ exc.motivo || '—' }}</td>
                          <td class="data-cell">{{ exc.dtinsercao || '—' }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
                </tbody>
                <tfoot v-if="excecoes.length">
                <tr class="exc-tfoot-row">
                  <td class="th-expand"></td>
                  <td></td>
                  <td class="exc-tfoot-label">Total geral</td>
                  <td class="centro exc-tfoot-total">{{ excecoes.length }}</td>
                </tr>
                </tfoot>
              </table>
            </div>

            <div v-if="excecoes.length" class="tabela-footer">
              <span>
                {{ excecoesPorFilial.length }} filial{{ excecoesPorFilial.length !== 1 ? 'is' : '' }}
                · {{ excecoes.length }} cliente{{ excecoes.length !== 1 ? 's' : '' }}
              </span>
              <v-pagination
                  v-if="totalPaginasExcecoes > 1"
                  v-model="paginaExcecoes"
                  :length="totalPaginasExcecoes"
                  :total-visible="7"
                  class="tabela-paginacao"
              />
            </div>
          </v-card>

        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- ══ Indicador: Falhas de Integração ══════════════════════════════════ -->
      <v-expansion-panel class="indicador-panel">

        <v-expansion-panel-header class="indicador-header" hide-actions>
          <template #default="{ open }">
            <div class="indicador-header-content">

              <div class="indicador-titulo-wrap">
                <v-icon size="18" color="#4a7a96" class="mr-2">mdi-api-off</v-icon>
                <span class="indicador-titulo">Falhas de Integração</span>
                <v-icon size="16" color="#94a3b8" class="ml-2 indicador-chevron" :class="{ 'indicador-chevron--open': open }">mdi-chevron-down</v-icon>
              </div>

              <div class="indicador-centro">
                <div v-if="falhas.length" class="indicador-resumo-wrap">
                  <v-chip x-small class="header-chip" :class="{ 'header-chip--neg': falhasVendas > 0 }">
                    <span class="header-chip-label">Vendas</span>
                    <span class="header-chip-valor">{{ falhasVendas }}</span>
                  </v-chip>
                  <v-chip x-small class="header-chip" :class="{ 'header-chip--neg': falhasDevolucoes > 0 }">
                    <span class="header-chip-label">Devoluções</span>
                    <span class="header-chip-valor">{{ falhasDevolucoes }}</span>
                  </v-chip>
                </div>
                <div v-else-if="loadingFalhas && !open" class="indicador-carregando">
                  <v-progress-circular indeterminate size="14" width="2" color="#4a7a96" class="mr-2" />
                  <span>Carregando...</span>
                </div>
                <div v-else-if="!open" class="indicador-sem-dados">
                  <v-icon size="13" color="#b0c4d4" class="mr-1">mdi-check-circle-outline</v-icon>
                  <span>Nenhuma falha encontrada</span>
                </div>
              </div>

              <v-btn icon x-small class="indicador-refresh" :loading="loadingFalhas" :disabled="loadingFalhas" @click.stop="buscarFalhas">
                <v-icon size="15" color="#7a9ab0">mdi-refresh</v-icon>
              </v-btn>

            </div>
          </template>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="indicador-content">
          <v-card elevation="0" outlined class="tabela-card">
            <v-progress-linear indeterminate absolute :active="loadingFalhas" color="primary" />

            <div v-if="loadingFalhas && !falhas.length" class="estado-vazio">
              <v-progress-circular indeterminate color="primary" size="36" />
              <span class="estado-texto">Carregando...</span>
            </div>

            <div v-else-if="!falhas.length" class="estado-vazio">
              <v-icon size="40" color="#b0c4d4">mdi-check-circle-outline</v-icon>
              <span class="estado-texto">Nenhuma falha de integração</span>
            </div>

            <div v-else class="tabela-scroll-wrap">
              <table class="audit-table">
                <thead>
                <tr>
                  <th class="th-expand"></th>
                  <th style="width:90px">Tipo</th>
                  <th class="num" style="width:80px"># Pedido</th>
                  <th>Cliente</th>
                  <th>Filial</th>
                  <th class="num">Inserção</th>
                  <th class="num">Emissão</th>
                  <th class="num" style="width:60px">Tent.</th>
                  <th style="width:90px"></th>
                </tr>
                </thead>
                <tbody>
                <template v-for="(falha, i) in falhas">
                  <tr :key="`f-${i}`" class="grupo-row" @click="toggleExpandFalha(i)">
                    <td class="expand-td">
                      <v-icon size="16" color="#7a9ab0" class="expand-icon" :class="{ 'expand-icon--open': expandidoFalha[i] }">
                        mdi-chevron-right
                      </v-icon>
                    </td>
                    <td>
                        <span :class="falha.tipo === 'VENDA' ? 'badge-venda' : 'badge-devolucao'">
                          {{ falha.tipo === 'VENDA' ? 'Venda' : 'Devolução' }}
                        </span>
                    </td>
                    <td class="num id-cell">{{ falha.idorcamento }}</td>
                    <td style="font-size:12px;">{{ falha.nome_cliente }}</td>
                    <td style="font-size:12px; color:#2c5f7a;">{{ falha.filial }}</td>
                    <td class="num data-cell">{{ falha.dtinsercao }}</td>
                    <td class="num data-cell">{{ falha.dtemissao }}</td>
                    <td class="num">
                      <span class="tentativas-badge">{{ falha.tentativas }}</span>
                    </td>
                    <td>
                      <v-btn
                          x-small text color="primary"
                          :loading="reprocessando[i]"
                          :disabled="reprocessando[i]"
                          @click.stop="reprocessarFalha(falha, i)"
                      >
                        <v-icon x-small left>mdi-refresh</v-icon>
                        Reprocessar
                      </v-btn>
                    </td>
                  </tr>

                  <tr v-if="expandidoFalha[i]" :key="`fl-${i}`" class="expand-row">
                    <td colspan="9" class="expand-cell">
                      <div v-if="loadingLogs[i]" class="estado-vazio" style="padding:12px">
                        <v-progress-circular indeterminate color="primary" size="24" />
                      </div>
                      <div v-else-if="!logsFalha[i] || !logsFalha[i].length" class="estado-vazio" style="padding:12px">
                        <span style="font-size:12px; color:#94a3b8;">Nenhum log encontrado</span>
                      </div>
                      <table v-else class="prod-table">
                        <thead>
                        <tr>
                          <th class="num" style="width:60px">Log</th>
                          <th style="width:130px">Data Envio</th>
                          <th style="width:70px">HTTP</th>
                          <th>Payload</th>
                          <th>Retorno</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(log, j) in logsFalha[i]" :key="j">
                          <td class="num prod-cod">{{ log.idlog }}</td>
                          <td class="data-cell">{{ log.dtenvio }}</td>
                          <td class="num">
                                <span :class="log.statushttp >= 200 && log.statushttp < 300 ? 'http-ok' : 'http-erro'">
                                  {{ log.statushttp }}
                                </span>
                          </td>
                          <td class="log-json-cell log-json-clicavel" @click.stop="abrirLogDetalhe('Payload', log.payload)">{{ log.payload }}</td>
                          <td class="log-json-cell log-json-clicavel" @click.stop="abrirLogDetalhe('Retorno', log.retorno)">{{ log.retorno }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>

            <div v-if="falhas.length" class="tabela-footer">
              <span>{{ falhas.length }} registro{{ falhas.length !== 1 ? 's' : '' }} com falha</span>
            </div>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

    </v-expansion-panels>

    <!-- Dialog: visualização completa de payload/retorno -->
    <v-dialog v-model="dialogLog.aberto" max-width="760" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between py-3 px-4" style="border-bottom:1px solid #e2e8f0;">
          <span style="font-size:14px; font-weight:600; color:#1e3a4a;">{{ dialogLog.titulo }}</span>
          <v-btn icon small @click="dialogLog.aberto = false">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <pre class="log-detalhe-pre">{{ dialogLog.conteudo }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import mixinFilial from '@/util/mixinFilial'
import SeletorFiliais from '@/Components/UI/Inputs/SeletorFiliais.vue'
import SeletorPeriodo from '@/Components/UI/Inputs/SeletorPeriodo.vue'
import SeletorCliente from '@/Components/UI/Inputs/SeletorCliente.vue'

export default {
  name: 'WolihubAuditoria',

  mixins: [mixinFilial],

  components: { SeletorFiliais, SeletorPeriodo, SeletorCliente },

  data: () => ({
    painelAberto: [],
    busca: {
      idfiliais:   [],
      idorcamento: null,
      cliente:     null,
      periodo: (() => {
        const hoje = new Date()
        const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
        const pad = n => String(n).padStart(2, '0')
        return {
          inicio: `${mesAnterior.getFullYear()}-${pad(mesAnterior.getMonth() + 1)}-01`,
          fim:    `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}-${pad(hoje.getDate())}`,
        }
      })(),
    },
    grupos:       [],
    filtroStatus: null,
    expandido:    {},
    reprocessando: {},
    loading:      false,
    buscado:      false,
    estoque:        [],
    loadingEstoque: false,
    sortEstoque:    { by: 'dias_estoque', desc: false },
    excecoes:        [],
    expandidoExc:    {},
    loadingExcecoes: false,
    falhas:          [],
    loadingFalhas:   false,
    expandidoFalha:  {},
    logsFalha:       {},
    loadingLogs:     {},
    dialogLog: { aberto: false, titulo: '', conteudo: '' },
    sortCreditos:  { by: null, desc: false },
    paginaCreditos: 1,
    sortExcecoes:  { by: 'clientes', desc: true },
    paginaExcecoes: 1,
    saldoClientes:           [],
    loadingSaldoClientes:    false,
    loadingMaisSaldoClientes: false,
    temMaisSaldoClientes:    false,
    sortSaldoClientes:       { by: null, desc: false },
    paginaSaldoClientes:     1,
    filtroSaldoClientes: {
      idempresa: [],
      cliente:   null,
    },
  }),

  computed: {
    ...mapState(['backendUrl', 'usuario']),

    filiaisWolihub() {
      if (!this.filiais.lista) return []
      const excluir = [4, 6, 12, 13, 22, 25, 28, 29, 33, 34, 37, 39, 40, 42, 43]
      return this.filiais.lista.filter(f => !excluir.includes(f.idfilial))
    },

    gruposFiltrados() {
      if (!this.filtroStatus) return this.grupos
      return this.grupos.filter(g => g.status_geral === this.filtroStatus)
    },

    totalItensFiltrados() {
      return this.gruposFiltrados.reduce((acc, g) => acc + ((g.itens && g.itens.length) || 0), 0)
    },

    totaisGlobais() {
      return this.grupos.reduce((acc, g) => {
        acc.gerado   += g.total_credito_gerado   || 0
        acc.esperado += g.total_credito_esperado || 0
        acc.diferenca += g.total_diferenca       || 0
        return acc
      }, { gerado: 0, esperado: 0, diferenca: 0 })
    },

    totalEstoqueQtd() {
      return this.estoque.reduce((acc, e) => acc + (e.qtd_estoque || 0), 0)
    },

    totalEstoqueVal() {
      return this.estoque.reduce((acc, e) => acc + (e.val_estoque || 0), 0)
    },

    totalEstoqueVendida() {
      return this.estoque.reduce((acc, e) => acc + (e.qtd_vendida_30d || 0), 0)
    },

    estoqueCriticas() {
      return this.estoque.filter(e => e.dias_estoque != null && e.dias_estoque < 7).length
    },

    estoqueOrdenado() {
      const { by, desc } = this.sortEstoque
      const lista = [...this.estoque]
      if (!by) return lista
      lista.sort((a, b) => {
        let va = a[by]
        let vb = b[by]
        if (va == null) va = desc ? Infinity : -Infinity
        if (vb == null) vb = desc ? Infinity : -Infinity
        const r = typeof va === 'string' ? va.localeCompare(vb) : va - vb
        return desc ? -r : r
      })
      return lista
    },

    gruposOrdenados() {
      const { by, desc } = this.sortCreditos
      const lista = [...this.gruposFiltrados]
      if (!by) return lista
      lista.sort((a, b) => {
        let va = by === 'itens' ? (a.itens || []).length : a[by]
        let vb = by === 'itens' ? (b.itens || []).length : b[by]
        if (va == null) va = ''
        if (vb == null) vb = ''
        const r = typeof va === 'string' ? va.localeCompare(vb) : va - vb
        return desc ? -r : r
      })
      return lista
    },

    gruposPaginados() {
      const s = (this.paginaCreditos - 1) * 50
      return this.gruposOrdenados.slice(s, s + 50)
    },

    totalPaginasCreditos() {
      return Math.ceil(this.gruposOrdenados.length / 50) || 1
    },

    excecoesPorFilial() {
      const grupos = {}
      this.excecoes.forEach(e => {
        const key = e.idempresa || 0
        if (!grupos[key]) grupos[key] = { idempresa: e.idempresa, filial: e.filial || 'Sem filial', clientes: [] }
        grupos[key].clientes.push(e)
      })
      return Object.values(grupos).sort((a, b) => (a.filial || '').localeCompare(b.filial || ''))
    },

    excecoesPorFilialOrdenadas() {
      const { by, desc } = this.sortExcecoes
      const lista = [...this.excecoesPorFilial]
      if (!by) return lista
      lista.sort((a, b) => {
        let va = by === 'clientes' ? a.clientes.length : by === 'idempresa' ? (a.idempresa || 0) : a.filial || ''
        let vb = by === 'clientes' ? b.clientes.length : by === 'idempresa' ? (b.idempresa || 0) : b.filial || ''
        if (va == null) va = ''
        if (vb == null) vb = ''
        const r = typeof va === 'string' ? va.localeCompare(vb) : va - vb
        return desc ? -r : r
      })
      return lista
    },

    excecoesPorFilialPaginadas() {
      const s = (this.paginaExcecoes - 1) * 50
      return this.excecoesPorFilialOrdenadas.slice(s, s + 50)
    },

    totalPaginasExcecoes() {
      return Math.ceil(this.excecoesPorFilialOrdenadas.length / 50) || 1
    },

    saldoClientesOrdenados() {
      const { by, desc } = this.sortSaldoClientes
      const lista = [...this.saldoClientes]
      if (!by) return lista
      lista.sort((a, b) => {
        let va = a[by]
        let vb = b[by]
        if (va == null) va = desc ? Infinity : -Infinity
        if (vb == null) vb = desc ? Infinity : -Infinity
        const r = typeof va === 'string' ? va.localeCompare(vb) : va - vb
        return desc ? -r : r
      })
      return lista
    },

    saldoClientesPaginados() {
      const s = (this.paginaSaldoClientes - 1) * 50
      return this.saldoClientesOrdenados.slice(s, s + 50)
    },

    totalPaginasSaldoClientes() {
      return Math.ceil(this.saldoClientesOrdenados.length / 50) || 1
    },

    totalSaldoClientes() {
      return this.saldoClientes.reduce((acc, c) => acc + (c.saldo || 0), 0)
    },

    saldoClientesAlerta() {
      return this.saldoClientes.filter(c => c.saldo >= 240).length
    },

    falhasVendas() {
      return this.falhas.filter(f => f.tipo === 'VENDA').length
    },

    falhasDevolucoes() {
      return this.falhas.filter(f => f.tipo === 'DEVOLUCAO').length
    },

    resumo() {
      const contagem = {}
      this.grupos.forEach(g => {
        contagem[g.status_geral] = (contagem[g.status_geral] || 0) + 1
      })
      const mapa = {
        'CREDITO_ORFAO': { label: 'Crédito Órfão', cor: 'orfao' },
        'DIVERGENCIA':   { label: 'Divergência',   cor: 'divergencia' },
        'PENDENTE':      { label: 'Pendente',       cor: 'pendente' },
        'OK':            { label: 'OK',             cor: 'ok' },
        'SEM DEVOLUCAO': { label: 'Sem Devolução', cor: 'sem-dev' },
        'EBOOK':         { label: 'E-book Dev.',    cor: 'ebook' },
      }
      const ordem = ['CREDITO_ORFAO', 'DIVERGENCIA', 'PENDENTE', 'OK', 'SEM DEVOLUCAO', 'EBOOK']
      return Object.entries(contagem)
          .map(([status, count]) => ({ status, count, ...(mapa[status] || { label: status, cor: 'default' }) }))
          .sort((a, b) => ordem.indexOf(a.status) - ordem.indexOf(b.status))
    },
  },

  created() {
    this.buscarEstoque()
    this.buscarExcecoes()
    this.buscarFalhas()
    this.buscarSaldoClientes()
  },

  watch: {
    'filiais.lista': {
      handler(lista) {
        if (lista && lista.length > 0 && this.busca.idfiliais.length === 0) {
          this.busca.idfiliais = this.filiaisWolihub.map(f => f.idfilial)
          this.buscar()
        }
      },
      immediate: true,
    },

    'filtroSaldoClientes.idempresa': { handler() { this.buscarSaldoClientes() }, deep: true },
    'filtroSaldoClientes.cliente'()   { this.buscarSaldoClientes() },
  },

  methods: {
    async buscar() {
      this.loading  = true
      this.buscado  = false
      this.expandido    = {}
      this.filtroStatus = null
      try {
        const payload = {
          idfiliais:   this.busca.idfiliais.length ? this.busca.idfiliais : null,
          idorcamento: this.busca.idorcamento ? parseInt(this.busca.idorcamento) : null,
          idclifor:    this.busca.cliente?.idcliente || null,
          dtinicio:    this.busca.periodo?.inicio || null,
          dtfim:       this.busca.periodo?.fim    || null,
        }
        const { data } = await axios.post(`${this.backendUrl}wolihub/auditoria/creditos`, payload)
        this.grupos = data || []
      } catch (e) {
        this.grupos = []
        const msg = e.response?.data?.erro || 'Erro ao buscar dados de auditoria'
        console.error(msg, e)
      } finally {
        this.loading = false
        this.buscado = true
      }
    },

    async reprocessar(grupo, i) {
      const planilhas = (grupo.itens || [])
          .filter(it => it.status === 'PENDENTE' && it.idplanilha_origem)
          .flatMap(it => it.idplanilha_origem.split(' | '))
          .filter((p, idx, arr) => p && arr.indexOf(p) === idx)

      if (!planilhas.length) return

      this.$set(this.reprocessando, i, true)
      try {
        await axios.post(`${this.backendUrl}wolihub/auditoria/reprocessar`, {
          idempresa:   grupo.idempresa,
          idclifor:    grupo.idclifor,
          idplanilhas: planilhas,
        })
        await this.buscar()
      } catch (e) {
        console.error('Erro ao reprocessar crédito:', e)
      } finally {
        this.$set(this.reprocessando, i, false)
      }
    },

    toggleFiltroStatus(status) {
      this.filtroStatus = this.filtroStatus === status ? null : status
      this.expandido = {}
      this.paginaCreditos = 1
    },

    ordenarCreditos(campo) {
      if (this.sortCreditos.by === campo) {
        this.sortCreditos = { by: campo, desc: !this.sortCreditos.desc }
      } else {
        this.sortCreditos = { by: campo, desc: false }
      }
      this.paginaCreditos = 1
      this.expandido = {}
    },

    ordenarExcecoes(campo) {
      if (this.sortExcecoes.by === campo) {
        this.sortExcecoes = { by: campo, desc: !this.sortExcecoes.desc }
      } else {
        this.sortExcecoes = { by: campo, desc: false }
      }
      this.paginaExcecoes = 1
      this.expandidoExc = {}
    },

    toggleExpand(i) {
      this.$set(this.expandido, i, !this.expandido[i])
    },

    toggleExpandExc(i) {
      this.$set(this.expandidoExc, i, !this.expandidoExc[i])
    },

    abrirLogDetalhe(titulo, conteudo) {
      this.dialogLog = { aberto: true, titulo, conteudo: conteudo || '—' }
    },

    async toggleExpandFalha(i) {
      const aberto = !this.expandidoFalha[i]
      this.$set(this.expandidoFalha, i, aberto)
      if (aberto && !this.logsFalha[i]) {
        this.$set(this.loadingLogs, i, true)
        try {
          const { data } = await axios.post(`${this.backendUrl}wolihub/falhas/logs`, {
            idcontrole: this.falhas[i].idcontrole,
            tipo:       this.falhas[i].tipo,
          })
          this.$set(this.logsFalha, i, data || [])
        } catch (e) {
          console.error('Erro ao carregar logs:', e)
        } finally {
          this.$set(this.loadingLogs, i, false)
        }
      }
    },

    async buscarFalhas() {
      this.loadingFalhas = true
      this.expandidoFalha = {}
      this.logsFalha = {}
      try {
        const { data } = await axios.post(`${this.backendUrl}wolihub/falhas/listar`)
        this.falhas = data || []
      } catch (e) {
        console.error('Erro ao carregar falhas:', e)
      } finally {
        this.loadingFalhas = false
      }
    },

    async reprocessarFalha(falha, i) {
      this.$set(this.reprocessando, i, true)
      try {
        await axios.post(`${this.backendUrl}wolihub/falhas/reprocessar`, {
          idcontrole: falha.idcontrole,
          tipo:       falha.tipo,
        })
        this.falhas.splice(i, 1)
        const novosExpandido = {}
        const novosLogs = {}
        Object.keys(this.expandidoFalha).forEach(k => {
          const ki = parseInt(k)
          if (ki < i) { novosExpandido[ki] = this.expandidoFalha[k]; novosLogs[ki] = this.logsFalha[k] }
          else if (ki > i) { novosExpandido[ki - 1] = this.expandidoFalha[k]; novosLogs[ki - 1] = this.logsFalha[k] }
        })
        this.expandidoFalha = novosExpandido
        this.logsFalha = novosLogs
      } catch (e) {
        console.error('Erro ao reprocessar:', e)
      } finally {
        this.$set(this.reprocessando, i, false)
      }
    },

    async buscarEstoque() {
      this.loadingEstoque = true
      try {
        const { data } = await axios.post(`${this.backendUrl}wolihub/estoque/saldo`)
        this.estoque = data || []
      } catch (e) {
        console.error('Erro ao carregar estoque:', e)
      } finally {
        this.loadingEstoque = false
      }
    },

    ordenarEstoque(campo) {
      if (this.sortEstoque.by === campo) {
        this.sortEstoque = { by: campo, desc: !this.sortEstoque.desc }
      } else {
        this.sortEstoque = { by: campo, desc: false }
      }
    },

    estoqueRowClass(item) {
      if (item.dias_estoque == null) return ''
      if (item.dias_estoque < 7)  return 'row-status--divergencia'
      if (item.dias_estoque < 15) return 'row-status--pendente'
      return ''
    },

    diasBadgeClass(dias) {
      if (dias < 7)  return 'dias-badge--critico'
      if (dias < 15) return 'dias-badge--baixo'
      if (dias < 45) return 'dias-badge--ok'
      return 'dias-badge--alto'
    },

    estoqueStatusBadge(item) {
      if (item.dias_estoque == null) return 'status-badge--sem-dev'
      if (item.dias_estoque < 7)    return 'status-badge--divergencia'
      if (item.dias_estoque < 15)   return 'status-badge--pendente'
      if (item.dias_estoque < 45)   return 'status-badge--ok'
      return 'status-badge--ebook'
    },

    estoqueStatusLabel(item) {
      if (item.dias_estoque == null) return 'Sem venda'
      if (item.dias_estoque < 7)    return 'Crítico'
      if (item.dias_estoque < 15)   return 'Baixo'
      if (item.dias_estoque < 45)   return 'OK'
      return 'Alto'
    },

    async buscarExcecoes() {
      this.loadingExcecoes = true
      try {
        const { data } = await axios.post(`${this.backendUrl}wolihub/excecao/listar`)
        this.excecoes = data || []
      } catch (e) {
        console.error('Erro ao carregar exceções:', e)
      } finally {
        this.loadingExcecoes = false
      }
    },

    async buscarSaldoClientes() {
      this.loadingSaldoClientes = true
      this.saldoClientes = []
      this.temMaisSaldoClientes = false
      this.paginaSaldoClientes = 1
      try {
        const payload = {
          offset:    0,
          idempresa: this.filtroSaldoClientes.idempresa.length ? this.filtroSaldoClientes.idempresa[0] : null,
          idclifor:  this.filtroSaldoClientes.cliente ? this.filtroSaldoClientes.cliente.idcliente : null,
        }
        const { data } = await axios.post(`${this.backendUrl}wolihub/saldo/clientes`, payload)
        this.saldoClientes = data || []
        this.temMaisSaldoClientes = (data || []).length === 50
      } catch (e) {
        console.error('Erro ao carregar saldo clientes:', e)
      } finally {
        this.loadingSaldoClientes = false
      }
    },

    async carregarMaisSaldoClientes() {
      this.loadingMaisSaldoClientes = true
      try {
        const payload = {
          offset:    this.saldoClientes.length,
          idempresa: this.filtroSaldoClientes.idempresa.length ? this.filtroSaldoClientes.idempresa[0] : null,
          idclifor:  this.filtroSaldoClientes.cliente ? this.filtroSaldoClientes.cliente.idcliente : null,
        }
        const { data } = await axios.post(`${this.backendUrl}wolihub/saldo/clientes`, payload)
        this.saldoClientes = [...this.saldoClientes, ...(data || [])]
        this.temMaisSaldoClientes = (data || []).length === 50
      } catch (e) {
        console.error('Erro ao carregar mais saldo clientes:', e)
      } finally {
        this.loadingMaisSaldoClientes = false
      }
    },

    ordenarSaldoClientes(campo) {
      if (this.sortSaldoClientes.by === campo) {
        this.sortSaldoClientes.desc = !this.sortSaldoClientes.desc
      } else {
        this.sortSaldoClientes.by   = campo
        this.sortSaldoClientes.desc = true
      }
      this.paginaSaldoClientes = 1
    },

    saldoClienteRowClass(item) {
      if (!item.saldo) return ''
      if (item.saldo >= 280) return 'row-status--orfao'
      if (item.saldo >= 240) return 'row-status--divergencia'
      return ''
    },

    saldoClienteBarColor(item) {
      if (!item.saldo) return 'grey lighten-2'
      const pct = (item.saldo / 300) * 100
      if (pct >= 93) return '#ef4444'
      if (pct >= 80) return '#f97316'
      if (pct >= 60) return '#eab308'
      return '#22c55e'
    },

    statusCor(status) {
      const mapa = {
        'DIVERGENCIA':   'divergencia',
        'PENDENTE':      'pendente',
        'CREDITO_ORFAO': 'orfao',
        'SEM DEVOLUCAO': 'sem-dev',
        'OK':            'ok',
        'EBOOK':         'ebook',
      }
      return mapa[status] || 'default'
    },

    statusLabel(status) {
      const mapa = {
        'CREDITO_ORFAO': 'Órfão',
        'DIVERGENCIA':   'Divergência',
        'PENDENTE':      'Pendente',
        'SEM DEVOLUCAO': 'Sem Dev.',
        'OK':            'OK',
        'EBOOK':         'E-book',
      }
      return mapa[status] || status || '—'
    },

    sortIcon(sortKey, campo) {
      const s = this[sortKey]
      if (s.by !== campo) return '⇅'
      return s.desc ? '↓' : '↑'
    },

    diferencaClass(val) {
      if (val === null || val === undefined) return ''
      if (val > 0.01)  return 'dif-positivo'
      if (val < -0.01) return 'dif-negativo'
      return ''
    },
  },
}
</script>

<style scoped>
.auditoria-wrap {
  padding: 0 2px;
}

/* ── Cabeçalho da página ────────────────────────────────────────────────── */
.page-toolbar {
  border: 1px solid #dde8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 4px rgba(44, 95, 122, 0.07) !important;
  margin-bottom: 16px;
  overflow: hidden;
}

.page-icon-wrapper {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #023449;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.page-subtitle {
  font-size: 0.813rem;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.2;
  font-weight: 400;
}

/* ── Expansion panels container ────────────────────────────────────────── */
.indicadores-panels {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.indicadores-panels >>> .v-expansion-panels {
  flex-direction: column;
  gap: 8px;
}

.indicador-panel {
  border: 1px solid #dde8f0 !important;
  border-radius: 10px !important;
  overflow: hidden;
}

.indicador-panel::before {
  box-shadow: none !important;
}

/* ── Header do painel ───────────────────────────────────────────────────── */
.indicador-header {
  background: #f8fbfd !important;
  min-height: 60px !important;
  padding: 0 16px !important;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s;
}

/* borda inferior visível quando minimizado */
.indicador-panel:not(.v-expansion-panel--active) .indicador-header {
  border-bottom-color: transparent;
}

.indicador-panel.v-expansion-panel--active .indicador-header {
  border-bottom: 1px solid #dde8f0;
  background: #f0f6fa !important;
}

.indicador-header-content {
  display: grid;
  grid-template-columns: 260px 1fr auto;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.indicador-titulo-wrap {
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
}

.indicador-titulo-wrap .indicador-chevron {
  margin-left: auto !important;
  flex-shrink: 0;
}

.indicador-centro {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
  justify-content: flex-end;
}

.indicador-refresh {
  flex-shrink: 0;
}

.indicador-titulo {
  font-size: 14.5px;
  font-weight: 600;
  color: #2c5f7a;
  letter-spacing: 0.01em;
}

.indicador-chevron {
  transition: transform 0.25s ease;
}
.indicador-chevron--open {
  transform: rotate(180deg);
}

.indicador-resumo-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
}

.indicador-sem-dados {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #b0c4d4;
}

.tfoot-row {
  background: #f0f5f8;
  border-top: 2px solid #d0dde6;
}

.tfoot-label {
  font-size: 11px;
  font-weight: 700;
  color: #4a6b80;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 10px;
}

.tfoot-val {
  font-weight: 700;
  color: #1e3a4a;
  padding: 6px 10px;
}

.limite-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
}

.limite-bar {
  flex: 1;
}

.limite-pct {
  font-size: 11px;
  font-weight: 600;
  color: #4a6b80;
  width: 32px;
  text-align: right;
  flex-shrink: 0;
}

.chips-status-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.indicador-carregando {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #4a7a96;
}

/* ── Conteúdo do painel ─────────────────────────────────────────────────── */
.indicador-content >>> .v-expansion-panel-content__wrap {
  padding: 16px 16px 12px;
}

/* chips mini no header (não clicáveis para abrir/fechar) */
.resumo-chip--mini {
  font-size: 11px !important;
  padding: 2px 9px !important;
}

/* ── Totais monetários no header ────────────────────────────────────────── */
.header-chip.v-chip {
  background: #e8f2f8 !important;
  border: 1px solid #c8dde9 !important;
  border-radius: 8px !important;
  padding: 0 !important;
  height: 28px !important;
  overflow: hidden;
}

.header-chip.v-chip >>> .v-chip__content {
  padding: 0;
  gap: 0;
}

.header-chip-label {
  font-size: 10px;
  font-weight: 500;
  color: #5a8aa0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(44, 95, 122, 0.10);
  padding: 0 8px;
  height: 100%;
  display: flex;
  align-items: center;
  border-right: 1px solid #c8dde9;
}

.header-chip-valor {
  font-size: 13px;
  font-weight: 700;
  color: #2c5f7a;
  font-variant-numeric: tabular-nums;
  font-family: 'Roboto Mono', monospace, sans-serif;
  padding: 0 9px;
}

.header-chip--pos.v-chip { background: #dcfce7 !important; border-color: #86efac !important; }
.header-chip--pos .header-chip-label { background: rgba(22, 163, 74, 0.10); border-color: #86efac; }
.header-chip--pos .header-chip-valor { color: #16a34a; }
.header-chip--neg.v-chip { background: #fee2e2 !important; border-color: #fca5a5 !important; }
.header-chip--neg .header-chip-label { background: rgba(220, 38, 38, 0.10); border-color: #fca5a5; }
.header-chip--neg .header-chip-valor { color: #dc2626; }

.header-separador {
  width: 1px;
  height: 28px;
  background: #dde8f0;
  margin: 0 4px;
  flex-shrink: 0;
}

/* ── Campo filtro padrão ────────────────────────────────────────────────── */
.filtro-field >>> .v-input__control { min-height: 44px !important; }
.filtro-field >>> fieldset {
  border-color: #cbd5e1;
  border-width: 2px;
  transition: all 0.2s ease;
}
.filtro-field >>> .v-input__slot {
  background: #f8fafc;
  border-radius: 10px;
  transition: all 0.2s ease;
}
.filtro-field >>> .v-input__slot:hover fieldset { border-color: #023449; }
.filtro-field >>> .v-input--is-focused fieldset {
  border-color: #023449 !important;
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(2, 52, 73, 0.1);
}
.filtro-field >>> .v-input--is-focused .v-input__slot { background: #ffffff !important; }

/* ── Resumo ─────────────────────────────────────────────────────────────── */
.resumo-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.resumo-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: filter 0.15s, box-shadow 0.15s;
  user-select: none;
}
.resumo-chip:hover { filter: brightness(0.95); }
.resumo-chip--ativo {
  box-shadow: 0 0 0 2px currentColor;
  filter: brightness(0.93);
}

.resumo-chip-count {
  font-weight: 700;
  font-size: 13px;
}

.resumo-chip--orfao       { background: #fdf4ff; border-color: #d8b4fe; color: #7c3aed; }
.resumo-chip--divergencia { background: #fef2f2; border-color: #fca5a5; color: #dc2626; }
.resumo-chip--pendente    { background: #fffbeb; border-color: #fcd34d; color: #b45309; }
.resumo-chip--ok          { background: #f0fdf4; border-color: #86efac; color: #15803d; }
.resumo-chip--sem-dev     { background: #f8fafc; border-color: #cbd5e1; color: #64748b; }
.resumo-chip--ebook       { background: #f0f9ff; border-color: #7dd3fc; color: #0369a1; }

/* ── Tabela ─────────────────────────────────────────────────────────────── */
.tabela-card {
  border-radius: 10px !important;
  overflow: hidden;
}

.tabela-scroll-wrap {
  overflow-x: auto;
}

/* ── Tabela principal (grupos/pedidos) ───────────────────────────────────── */
.audit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.audit-table thead th {
  background: #f0f6fa;
  color: #5b7a8e;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 9px 12px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #dde8f0;
}

.audit-table th.num,
.audit-table td.num { text-align: right; }

.audit-table th.centro,
.audit-table td.centro { text-align: center; }

.audit-table .th-expand {
  width: 36px;
  padding: 0 !important;
}

.audit-table td {
  padding: 8px 12px;
  color: #374151;
  border-bottom: 1px solid #f0f4f8;
  white-space: nowrap;
  vertical-align: middle;
}

.audit-table td.moeda {
  font-variant-numeric: tabular-nums;
  font-family: 'Roboto Mono', monospace, sans-serif;
}

/* IDs numéricos (empresa, pedido, cliente) */
.audit-table .id-cell {
  font-family: 'Roboto Mono', monospace, sans-serif;
  font-size: 11.5px;
  color: #4a7a96;
}

/* Linha do grupo — clicável */
.grupo-row {
  cursor: pointer;
  transition: background 0.12s;
}
.grupo-row:hover { filter: brightness(0.97); }

/* Ícone de expand */
.expand-td {
  width: 36px;
  padding: 0 4px 0 10px !important;
  text-align: center;
}
.expand-icon {
  transition: transform 0.2s ease;
  display: block;
}
.expand-icon--open { transform: rotate(90deg); }

/* Status + botão reprocessar na mesma célula */
.status-td {
  white-space: nowrap;
}

/* Contador de itens */
.itens-count {
  display: inline-block;
  background: #e2edf4;
  color: #4a7a96;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
}

/* Cores por status — linha principal */
.row-status--orfao       { background: #fdf8ff; }
.row-status--divergencia { background: #fff8f8; }
.row-status--pendente    { background: #fffdf5; }
.row-status--sem-dev     { background: #fafafa; }

/* ── Linha expandida ─────────────────────────────────────────────────────── */
.expand-row > .expand-cell {
  padding: 0 !important;
  background: #f4f8fb;
  border-bottom: 2px solid #dde8f0;
  border-left: 3px solid #b8d4e8;
}

/* ── Tabela de produtos (interior) ──────────────────────────────────────── */
.prod-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11.5px;
}

.prod-table thead th {
  background: #eaf2f8;
  color: #4a7a96;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 7px 12px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #cdd8e3;
}

.prod-table th.num,
.prod-table td.num { text-align: right; }

.prod-table tbody tr {
  border-bottom: 1px solid #e8f0f6;
}

.prod-table tbody tr:last-child { border-bottom: none; }

.prod-table td {
  padding: 6px 12px;
  color: #374151;
  white-space: nowrap;
  vertical-align: middle;
}

.prod-table td.moeda {
  font-variant-numeric: tabular-nums;
  font-family: 'Roboto Mono', monospace, sans-serif;
}

/* Código do produto — leve destaque mono */
.prod-cod {
  font-family: 'Roboto Mono', monospace, sans-serif;
  font-size: 11px;
  color: #4a7a96;
  font-weight: 600;
}

/* Data de devolução */
.data-cell {
  font-variant-numeric: tabular-nums;
  color: #64748b;
  font-size: 11px;
}

.planilha-cell {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10.5px;
  color: #64748b;
}

/* Cores linhas de produto */
.prod-row--orfao       { background: #fdf4ff; }
.prod-row--divergencia { background: #fef2f2; }
.prod-row--pendente    { background: #fffbeb; }
.prod-row--sem-dev     { background: #f8fafc; }

/* ── Badges de status ────────────────────────────────────────────────────── */
.status-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.status-badge--sm { font-size: 9.5px; padding: 1px 6px; }

.status-badge--orfao       { background: #ede9fe; color: #7c3aed; }
.status-badge--divergencia { background: #fee2e2; color: #dc2626; }
.status-badge--pendente    { background: #fef3c7; color: #d97706; }
.status-badge--ok          { background: #dcfce7; color: #16a34a; }
.status-badge--sem-dev     { background: #f1f5f9; color: #64748b; }
.status-badge--ebook       { background: #e0f2fe; color: #0369a1; }

/* ── Diferença colorida ──────────────────────────────────────────────────── */
.dif-positivo { color: #16a34a; font-weight: 600; }
.dif-negativo { color: #dc2626; font-weight: 600; }

/* ── Estado vazio / loading ──────────────────────────────────────────────── */
.estado-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
}

.estado-texto {
  font-size: 13px;
  color: #8fa8ba;
}

/* ── Dias de estoque ─────────────────────────────────────────────────────── */
.dias-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.dias-badge--critico { background: #fee2e2; color: #dc2626; }
.dias-badge--baixo   { background: #fef3c7; color: #d97706; }
.dias-badge--ok      { background: #dcfce7; color: #16a34a; }
.dias-badge--alto    { background: #e0f2fe; color: #0369a1; }

/* ── Exceções de Clientes ────────────────────────────────────────────────── */
.exc-filial-cell {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: #2c5f7a;
}

.exc-usuario-cell {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #4a7a96;
  white-space: nowrap;
}

.exc-nome-cell {
  font-size: 12px;
  font-weight: 600;
  color: #023449;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.exc-motivo-cell {
  font-size: 11px;
  color: #64748b;
  font-style: italic;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.exc-tfoot-row {
  background: #f0f6fa;
  border-top: 2px solid #dde8f0;
}
.exc-tfoot-label {
  font-size: 12px;
  font-weight: 700;
  color: #2c5f7a;
  padding: 8px 12px;
}
.exc-tfoot-total {
  font-size: 13px;
  font-weight: 700;
  color: #023449;
  padding: 8px 12px;
}

/* ── Falhas de Integração ────────────────────────────────────────────────── */
.badge-venda {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-devolucao {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  background: #fef3c7;
  color: #b45309;
}

.tentativas-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
  background: #fee2e2;
  color: #dc2626;
}

.http-ok {
  font-size: 11px;
  font-weight: 600;
  color: #16a34a;
}

.http-erro {
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
}

.log-json-cell {
  font-size: 10px;
  font-family: 'Roboto Mono', monospace, sans-serif;
  color: #475569;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-json-clicavel {
  cursor: pointer;
}
.log-json-clicavel:hover {
  color: #2c5f7a;
  text-decoration: underline dotted;
}

.log-detalhe-pre {
  font-size: 12px;
  font-family: 'Roboto Mono', monospace, sans-serif;
  color: #1e293b;
  background: #f8fbfd;
  padding: 16px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 520px;
  overflow-y: auto;
}

/* ── Ordenação ───────────────────────────────────────────────────────────── */
.th-sort {
  cursor: pointer;
  user-select: none;
}
.th-sort:hover { color: #2c5f7a; }

.sort-icon {
  font-size: 10px;
  opacity: 0.5;
  margin-left: 2px;
}
.th-sort:hover .sort-icon { opacity: 0.9; }

/* ── Rodapé ──────────────────────────────────────────────────────────────── */
.tabela-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 4px 16px;
  font-size: 11px;
  color: #8fa8ba;
  border-top: 1px solid #f0f4f8;
  flex-wrap: wrap;
  gap: 4px;
}

.tabela-paginacao {
  margin: 0;
}

.tabela-paginacao >>> .v-pagination__item,
.tabela-paginacao >>> .v-pagination__navigation {
  height: 28px !important;
  min-width: 28px !important;
  font-size: 11px !important;
  box-shadow: none !important;
}

.tabela-paginacao >>> .v-pagination__item--active {
  background: #023449 !important;
}

/* ── Responsividade mobile ───────────────────────────────────────────────── */
@media (max-width: 600px) {

  /* Cabeçalho da página */
  .page-toolbar {
    height: auto !important;
    min-height: 60px;
  }

  .page-icon-wrapper {
    padding: 7px;
    border-radius: 10px;
  }

  .page-icon-wrapper >>> .v-icon {
    font-size: 20px !important;
  }

  .page-title {
    font-size: 1rem;
  }

  .page-subtitle {
    font-size: 0.75rem;
  }

  /* Header dos painéis: empilha título em cima, chips embaixo */
  .indicador-header-content {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    row-gap: 6px;
  }

  .indicador-titulo-wrap {
    grid-column: 1;
    grid-row: 1;
  }

  .indicador-refresh {
    grid-column: 2;
    grid-row: 1;
    align-self: start;
  }

  .indicador-centro {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-content: flex-start;
  }

  .indicador-resumo-wrap {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  /* Header com padding vertical para acomodar chips em duas linhas */
  .indicador-header {
    min-height: 70px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }

  /* Chips menores e mais compactos */
  .header-chip.v-chip {
    height: 22px !important;
  }

  .header-chip-label {
    font-size: 9px;
    padding: 0 5px;
  }

  .header-chip-valor {
    font-size: 11px;
    padding: 0 6px;
  }

  .indicador-resumo-wrap {
    gap: 4px;
  }

  /* Tabelas */
  .audit-table {
    font-size: 11px;
  }

  .audit-table thead th {
    font-size: 10px;
    padding: 6px 8px;
  }

  .audit-table tbody td {
    padding: 6px 8px;
  }

  /* Rodapé da tabela */
  .tabela-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
  }

  /* Título do painel */
  .indicador-titulo {
    font-size: 13px;
  }
}
</style>
